import Button from '@/components/Button';
import { useAppSelector } from '@/store';
import { teal900 } from '@/theme/colors';

import { STEP_COPY } from '../constants';
import type { useSelfServiceSMBSignupFlow } from '../hooks/useSelfServiceSMBSignupFlow';
import { useStyles } from './ConfirmationStep.styles';

interface ConfirmationStepProps {
  flow: ReturnType<typeof useSelfServiceSMBSignupFlow>;
  onGoToDashboard: () => void;
  ctaLabel?: string;
}

export function ConfirmationStep({
  flow,
  onGoToDashboard,
  ctaLabel = STEP_COPY.confirmation.ctaDashboard,
}: ConfirmationStepProps): JSX.Element {
  const s = useStyles();
  const { state } = flow;
  // On resume, the user's companyName isn't in state — pull it from viewer
  // so the bullet list reads "Acme team created" instead of "Your team team
  // created".
  const viewerTeamName = useAppSelector((rs) => rs.viewer.groups?.[0]?.name);
  const teamName = state.account.companyName?.trim() || viewerTeamName?.trim() || 'Your team';
  const trialDays = state.subscriptionResult?.trialDaysGranted ?? 0;
  const trialActive = trialDays > 0;
  const title = trialActive ? STEP_COPY.confirmation.titleTrial : STEP_COPY.confirmation.titlePaid;
  const subtitle = trialActive
    ? STEP_COPY.confirmation.subtitleTrial(trialDays)
    : STEP_COPY.confirmation.subtitlePaid;
  const bullets: ReadonlyArray<string> = [
    'Account created',
    `${teamName} team created`,
    'Pro Subscription activated',
  ];

  return (
    <>
      <div className={s.content}>
        <div className={s.icon}>
          <svg width="32" height="32" viewBox="0 0 20 20" fill={teal900} aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h2 id="self-service-smb-confirmation-title" className={s.title}>
          {title}
        </h2>
        <p className={s.subtitle}>{subtitle}</p>
        <div className={s.bulletsBox}>
          <ul className={s.bulletsList}>
            {bullets.map((label) => (
              <li key={label} className={s.bulletItem}>
                <span className={s.bulletCheckmark} aria-hidden="true">
                  ✓
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={s.footer}>
        <Button
          color="teal"
          size="normal"
          onClick={onGoToDashboard}
          classes={{ root: s.dashboardButton }}
        >
          {ctaLabel}
        </Button>
      </div>
    </>
  );
}
